import {AppUrls} from 'shared/config/model/urls';

import {useCallback} from 'react';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {
    getMonitorLifecycleTexts,
    invalidateDomainIncidents,
    useMonitorDelete,
    useMonitorPause,
    useMonitorResume,
} from 'entities/monitor';
import {httpIncidentAPI} from 'features/http/api/httpIncidentAPI';
import {httpMonitorAPI} from 'features/http/api/httpMonitorAPI';
import {reachGoal, YM_GOALS} from 'shared/lib/analytics';
import {TypedDispatch} from 'shared/model/types';

import IHttpMonitorOutput from './HttpMonitor/IHttpMonitor';

interface IMutationParams {
    item: Pick<IHttpMonitorOutput, 'uuid'>,
    handleClose: () => void,
}

export const useHttpMonitorPause = ({item, handleClose}: IMutationParams) => {
    const [stopHttpMonitor, {isLoading}] = httpMonitorAPI.useStopHttpMonitorMutation();

    return useMonitorPause({
        request: () => stopHttpMonitor(item.uuid).unwrap(),
        isLoading,
        handleClose,
        successText: getMonitorLifecycleTexts().paused,
    });
};

export type HttpMonitorResumeGoal = 'RESUME_MONITOR' | 'RESUME_MONITOR_DETAIL';

interface IResumeParams extends IMutationParams {
    goal?: HttpMonitorResumeGoal,
}

export const useHttpMonitorResume = ({item, handleClose, goal = 'RESUME_MONITOR'}: IResumeParams) => {
    const [startHttpMonitor, {isLoading}] = httpMonitorAPI.useStartHttpMonitorMutation();

    return useMonitorResume({
        request: () => {
            reachGoal(YM_GOALS[goal]);
            return startHttpMonitor(item.uuid).unwrap();
        },
        isLoading,
        handleClose,
        successText: getMonitorLifecycleTexts().resumed,
    });
};

export const useHttpMonitorCheckNow = (uuid: string) => {
    const [checkHttpMonitorNow] = httpMonitorAPI.useCheckHttpMonitorNowMutation();

    return useCallback(async() => {
        await checkHttpMonitorNow(uuid).unwrap();
        toast.info(t`Проверка монитора запущена`);
    }, [checkHttpMonitorNow, uuid]);
};

const invalidateHttpIncidents = (dispatch: TypedDispatch) => {
    dispatch(httpIncidentAPI.util.invalidateTags(['HttpIncident', 'SummaryHttpIncident']));
    invalidateDomainIncidents(dispatch);
};

export const useHttpMonitorDelete = ({item, handleClose}: IMutationParams) => {
    const [deleteHttpMonitor, {isLoading}] = httpMonitorAPI.useDeleteHttpMonitorMutation();

    return useMonitorDelete({
        request: () => deleteHttpMonitor(item.uuid).unwrap(),
        isLoading,
        handleClose,
        successText: getMonitorLifecycleTexts().deleted,
        listUrl: AppUrls.HTTP_MONITORS,
        detailsRoute: AppUrls.HTTP_MONITOR_DETAILS,
        withTags: true,
        onDeleted: invalidateHttpIncidents,
    });
};
